import React, { Suspense, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Outlet, useLocation, Navigate, useNavigate, useOutlet, Link } from 'react-router-dom';
import Sidebar from "@/components/SidebarNext";
import Logo from "@/components/Logo";
import { ThemeToggle } from "@/core/oblique-ui";
import { AppLauncher } from '@/core/oblique-ui';
import NotificationBell from "@/components/NotificationBell";
import ObliqueIcon from '@/components/icons/ObliqueIcon';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/CompanyContext';
import { useTenant } from '@/contexts/TenantContext';
import { useQuality } from '@/contexts/QualityContext';
import { useTheme } from '@/contexts/ThemeContext';
import MobileLayout from "@/components/MobileLayout";
import CreateTenantModal from '@/modules/Administration/components/settings/CreateTenantModal';
import WalkthroughTour from '@/components/shared/WalkthroughTour';
import ObliqueAIIcon from '@/components/icons/ObliqueAIIcon';
import { logger } from '@/lib/logger';
import { GlobalSearch, ProfileMenu, TenantLogo, Breadcrumb } from '@/core/oblique-ui';
import { coursesData } from '@/modules/LMS/data/coursesData';
import WorkspaceAvatar from '@/components/common/WorkspaceAvatar';
import ScaleAgencyLogo from '@/components/common/ScaleAgencyLogo';

const SYSTEM_MODULES = [
    { id: 'crm', path: '/crm', label: 'CRM Operations', icon: 'support_agent', color: '#3b82f6' },
    { id: 'quality', path: '/quality', label: 'QMS & Audit', icon: 'verified', color: '#059669' },
    { id: 'analytics', path: '/analytics', label: 'Intelligence Hub', icon: 'analytics', color: '#9333ea' },
    { id: 'lms', path: '/lms', label: 'Knowledge/LMS', icon: 'school', color: '#f59e0b' },
    { id: 'orbit', path: '/automations', label: 'Automations', icon: 'all_inclusive', color: '#ec4899' },
    { id: 'ai', path: '/ai', label: 'Oblique AI', icon: 'auto_awesome', color: '#8b5cf6' },
];

function getModuleTheme(pathname) {
    if (pathname.startsWith('/analytics')) return 'theme-analytics';
    if (pathname.startsWith('/quality')) return 'theme-quality';
    if (pathname.startsWith('/lms')) return 'theme-lms';
    if (pathname.startsWith('/automation')) return 'theme-automations';
    if (pathname.startsWith('/settings')) return 'theme-manager';
    if (pathname.startsWith('/hr')) return 'theme-hr';
    if (pathname.startsWith('/finance')) return 'theme-finance';
    if (pathname.startsWith('/developer')) return 'theme-developer';
    if (pathname.startsWith('/crm')) return 'theme-crm';
    if (pathname.startsWith('/ai')) return 'theme-ai';
    return 'theme-crm';
}

function getActiveModule(pathname) {
    if (pathname.startsWith('/quality/tracker')) return { name: 'HubSpot Alerts', color: '#059669', bg: 'rgba(5, 150, 105, 0.12)', icon: 'notifications_active' };
    if (pathname.startsWith('/quality')) return { name: 'QMS', color: '#059669', bg: 'rgba(5, 150, 105, 0.12)', icon: 'verified' };
    if (pathname.startsWith('/analytics')) return { name: 'Analytics', color: '#9333ea', bg: 'rgba(147, 51, 234, 0.1)', icon: 'analytics' };
    if (pathname.startsWith('/lms')) return { name: 'LMS', color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.12)', icon: 'school' };
    if (pathname.startsWith('/automation')) return { name: 'Automations', color: '#10b981', bg: 'rgba(16, 185, 129, 0.12)', icon: 'bolt' };
    if (pathname.startsWith('/settings')) return { name: 'Settings', color: '#8b5cf6', bg: 'rgba(139, 92, 246, 0.12)', icon: 'settings' };
    if (pathname.startsWith('/hr')) return { name: 'HR', color: '#f43f5e', bg: 'rgba(244, 63, 94, 0.12)', icon: 'groups' };
    if (pathname.startsWith('/finance')) return { name: 'Finance', color: '#14b8a6', bg: 'rgba(20, 184, 166, 0.12)', icon: 'payments' };
    if (pathname.startsWith('/developer')) return { name: 'Developer', color: '#10b981', bg: 'rgba(16, 185, 129, 0.12)', icon: 'code' };
    if (pathname.startsWith('/ai')) return { name: 'Oblique AI', color: '#8b5cf6', bg: 'rgba(139, 92, 246, 0.12)', icon: 'auto_awesome' };
    if (pathname.startsWith('/crm') || pathname === '/' || pathname.startsWith('/operations')) return { name: 'CRM', color: '#2563eb', bg: 'rgba(37, 99, 235, 0.1)', icon: 'support_agent' };
    return { name: 'System', color: '#64748b', bg: 'rgba(100, 116, 139, 0.1)', icon: 'home' };
}

const ExperimentNavButton = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isHovered, setIsHovered] = React.useState(false);
    
    return (
        <button
            onClick={() => navigate('/ai/phrase-library')}
            title="Phrase Library"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            className="relative w-[34px] h-[34px] p-0 rounded-lg bg-transparent border-none cursor-pointer text-[var(--text-secondary)] flex items-center justify-center"
        >
            <ObliqueAIIcon size={22} isHovered={isHovered} />
        </button>
    );
};

const FloatingAIAssistantButton = () => {
    const navigate = useNavigate();
    const [isHovered, setIsHovered] = React.useState(false);

    return (
        <motion.button
            onClick={() => navigate('/ai')}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.94 }}
            transition={{ type: "spring", stiffness: 400, damping: 25 }}
            title="Oblique AI Assistant"
            style={{
                position: 'fixed',
                bottom: '24px',
                right: '24px',
                zIndex: 9999
            }}
            className="w-12 h-12 rounded-full bg-[var(--bg-surface)] text-[var(--text-primary)] border border-[var(--border)] hover:border-[var(--accent)]/40 shadow-[0_4px_20px_rgba(0,0,0,0.08),0_1px_3px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgba(99,102,241,0.22)] flex items-center justify-center cursor-pointer transition-shadow duration-200 group relative"
        >
            <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-[var(--accent)]/0 via-[var(--accent)]/5 to-[var(--accent)]/15 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
            <ObliqueAIIcon size={24} isHovered={isHovered} />
        </motion.button>
    );
};

export default function Layout() {
    logger.debug('[Oblique] Layout component rendering');
    const location = useLocation();
    const navigate = useNavigate();
    const outlet = useOutlet();
    const { user, userData, loading, logout, userRole, showSmartTabs, saveUserSession } = useAuth();
    const { currentCompany, companies, switchCompany } = useCompany();

    // ═══════════════════════════════════════════════════════════════
    // SMART TAB ENGINE - State & Caching
    // ═══════════════════════════════════════════════════════════════
    const [tabs, setTabs] = React.useState(() => {
        const saved = localStorage.getItem('oblique_tabs');
        if (saved) return JSON.parse(saved);
        return [{ id: 'dashboard', title: 'Home', path: '/', active: true, icon: 'home' }];
    });
    const activeTab = tabs.find(t => t.path === location.pathname) || tabs.find(t => t.active) || tabs[0];

    const { currentTenant, tenants, switchTenant } = useTenant();
    const { auditCounts } = useQuality();
    const { theme } = useTheme();

    const activeLogo = useMemo(() => {
        if (theme === 'dark') {
            return currentCompany?.darkLogoUrl || currentTenant?.darkLogoUrl || currentCompany?.logoUrl || currentTenant?.lightLogoUrl || null;
        }
        return currentCompany?.lightLogoUrl || currentTenant?.lightLogoUrl || currentCompany?.logoUrl || currentTenant?.darkLogoUrl || null;
    }, [theme, currentCompany, currentTenant]);
    
    // UI States
    const [isProfilePopupOpen, setIsProfilePopupOpen] = React.useState(false);
    const [profileView, setProfileView] = React.useState('main'); // 'main' | 'settings'
    const [isCompanySwitcherOpen, setIsCompanySwitcherOpen] = React.useState(false);
    const [isTenantSwitcherOpen, setIsTenantSwitcherOpen] = React.useState(false);
    const [isCreateTenantModalOpen, setIsCreateTenantModalOpen] = React.useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
    const [isCommandPaletteOpen, setIsCommandPaletteOpen] = React.useState(false);
    const [paletteQuery, setPaletteQuery] = React.useState('');
    // 2-state sidebar: 'expanded' | 'hidden'
    const [sidebarMode, setSidebarMode] = React.useState(() => {
        const saved = localStorage.getItem('sidebar_mode');
        if (saved === 'hidden' || saved === 'expanded') return saved;
        return 'expanded';
    });
    
    // Collapsed icon-only mode
    const [isCollapsed, setIsCollapsed] = React.useState(() => {
        return localStorage.getItem('sidebar_collapsed') === 'true';
    });

    const isSettingsMode = location.pathname.startsWith('/settings') || location.pathname.startsWith('/admin');
    const isAIPlatform = location.pathname.startsWith('/ai');
    const isLMSCourse = location.pathname.startsWith('/lms/') && !['/lms/courses', '/lms/planner', '/lms/intelligence', '/lms/playbook'].includes(location.pathname);
    const isLMS = location.pathname.startsWith('/lms');
    const isHidden = isAIPlatform || (sidebarMode === 'hidden') || (isLMSCourse && sidebarMode !== 'expanded');

    const toggleSidebar = () => {
        const next = isHidden ? 'expanded' : 'hidden';
        setSidebarMode(next);
        localStorage.setItem('sidebar_mode', next);
    };

    const toggleCollapse = () => {
        const next = !isCollapsed;
        setIsCollapsed(next);
        localStorage.setItem('sidebar_collapsed', String(next));
    };

    const hideSidebar = () => {
        setSidebarMode('hidden');
        localStorage.setItem('sidebar_mode', 'hidden');
    };

    const themeClass = useMemo(() => getModuleTheme(location.pathname), [location.pathname]);
    const activeModule = useMemo(() => getActiveModule(location.pathname), [location.pathname]);

    // System Design Breadcrumb hierarchy for LMS (e.g. LMS > Assessment > Day 1)
    const lmsBreadcrumbItems = useMemo(() => {
        if (!isLMS) return [];
        const searchParams = new URLSearchParams(location.search);
        const tab = searchParams.get('tab');
        const view = searchParams.get('view');
        const action = searchParams.get('action');

        const items = [
            { label: 'LMS', path: '/lms' }
        ];

        if (location.pathname === '/lms' || location.pathname === '/lms/') {
            if (tab === 'assessments') {
                items.push({ label: 'Assessment', path: '/lms?tab=assessments' });
                if (view === 'scores') {
                    items.push({ label: 'Day 1', path: '/lms?tab=assessments' });
                    items.push({ label: 'Scorecards & Badges' });
                } else if (view === 'all') {
                    items.push({ label: 'All Scores' });
                } else {
                    items.push({ label: 'Day 1' });
                }
            } else if (tab === 'trainees') {
                items.push({ label: 'Trainees', path: '/lms?tab=trainees' });
                if (action === 'assign') {
                    items.push({ label: 'Assign Modules' });
                } else {
                    items.push({ label: 'Roster & Seats' });
                }
            } else {
                items.push({ label: 'Courses' });
            }
        } else if (location.pathname === '/lms/intelligence') {
            items.push({ label: 'Playbook', path: '/lms/intelligence' });
            items.push({ label: 'Search Intelligence' });
        } else if (location.pathname === '/lms/planner') {
            items.push({ label: 'Training Calendar' });
        } else if (location.pathname === '/lms/courses') {
            items.push({ label: 'Course Catalog' });
        } else if (location.pathname.startsWith('/lms/')) {
            const courseSlug = location.pathname.replace('/lms/', '');
            const course = coursesData[courseSlug] || coursesData['scale-agency-mastery'];
            const isExam = location.state?.takeExam || courseSlug.includes('exam');
            
            if (isExam) {
                items.push({ label: 'Assessment', path: '/lms?tab=assessments' });
                items.push({ label: 'Day 1' });
            } else {
                items.push({ label: 'Courses', path: '/lms?tab=tracks' });
                items.push({ label: course?.title ? (course.title.length > 25 ? `${course.title.slice(0, 25)}...` : course.title) : 'Lesson 1' });
            }
        }

        return items;
    }, [isLMS, location.pathname, location.search, location.state]);

    // 1. Removed outlet caching because it crashes React Router in production

    // 2. Persist Tab shell to localStorage
    React.useEffect(() => {
        localStorage.setItem('oblique_tabs', JSON.stringify(tabs.map(t => ({ ...t, element: null }))));
    }, [tabs]);

    // ⌘K / Ctrl+K — Command Palette Shortcut
    React.useEffect(() => {
        // Session Resume: On initial mount, if at home, try to resume last path
        if (location.pathname === '/' || location.pathname === '/hub') {
            const lastPath = localStorage.getItem('oblique_last_path');
            if (lastPath && lastPath !== '/' && lastPath !== '/hub') {
                navigate(lastPath, { replace: true });
            }
        }
    }, []);

    React.useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault();
                setIsCommandPaletteOpen(prev => !prev);
            }
            if (e.key === 'Escape' && isCommandPaletteOpen) {
                setIsCommandPaletteOpen(false);
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isCommandPaletteOpen]);


    // 3. Sync URL with Tabs + Persist last path for session resume + Module Switch Loader
    React.useEffect(() => {
        const currentPath = location.pathname;
        
        // Salesforce-style: persist last path for resume on next login
        if (currentPath !== '/' && currentPath !== '/hub' && currentPath !== '/login' && currentPath !== '/onboarding' && !currentPath.startsWith('/settings') && !currentPath.startsWith('/ai')) {
            localStorage.setItem('oblique_last_path', currentPath);
            if (saveUserSession) {
                saveUserSession({ path: currentPath, companyId: currentCompany?.id });
            }
        }
        
        const existingTab = tabs.find(t => t.path === currentPath);

        if (existingTab) {
            if (!existingTab.active) {
                setTabs(prev => prev.map(t => ({ ...t, active: t.path === currentPath })));
            }
        } else {
            const moduleInfo = getActiveModule(currentPath);
            const newTab = {
                id: Math.random().toString(36).substr(2, 9),
                title: moduleInfo.name,
                path: currentPath,
                active: true,
                icon: moduleInfo.icon
            };
            setTabs(prev => [...prev.map(t => ({ ...t, active: false })), newTab]);
        }
    }, [location.pathname]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen w-screen bg-[var(--bg-page)]">
                <div className="w-8 h-8 rounded-full border-[3px] border-[var(--border)] border-t-[var(--accent)] animate-spin" />
            </div>
        );
    }

    if (!user) return <Navigate to="/login" state={{ from: location }} replace />;

    const closeTab = (e, tabId) => {
        e.stopPropagation();
        if (tabs.length === 1) return;
        const tabToClose = tabs.find(t => t.id === tabId);
        const newTabs = tabs.filter(t => t.id !== tabId);
        if (tabToClose.active) {
            const lastTab = newTabs[newTabs.length - 1];
            lastTab.active = true;
            navigate(lastTab.path);
        }
        setTabs(newTabs);
    };

    const switchTab = (tab) => navigate(tab.path);

    const handleLogout = async () => {
        setIsProfilePopupOpen(false);
        if (logout) await logout();
        navigate('/login');
    };

    const menuItemStyle = {
        display: 'flex', alignItems: 'center', gap: '14px', width: '100%', padding: '12px 24px',
        background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)',
        fontSize: '15px', fontWeight: '500', textAlign: 'left', transition: 'background 0.15s'
    };

    return (
        <div className={`layout-container ${themeClass} font-['Lexend_Deca']`}>
            <div className={`sidebar-backdrop ${isMobileMenuOpen ? 'active' : ''}`} onClick={() => setIsMobileMenuOpen(false)} />
            
            {/* Sidebar — supports 2 modes: expanded, hidden */}
            {!isAIPlatform && (!isLMSCourse || sidebarMode === 'expanded') && (
                <Sidebar 
                    mobileOpen={isMobileMenuOpen} 
                    isCollapsed={isCollapsed} 
                    isHidden={isHidden}
                    isHovered={false}
                    onToggle={toggleSidebar}
                    onToggleCollapse={toggleCollapse}
                    onHide={() => {}}
                    onHoverStart={() => {}}
                    onHoverEnd={() => {}}
                />
            )}
            
            <div className={`flex-1 flex flex-col overflow-hidden transition-[margin-left] duration-150 ease-out ${isHidden ? 'ml-0' : (isCollapsed ? 'ml-16' : 'ml-[260px]')}`}>
                
                {!isAIPlatform && (
                <header className="glass-header desktop-only border-b border-[var(--border)] h-[54px] min-h-[54px] max-h-[54px] box-border pl-3 pr-5 flex items-center justify-between">
                    <button className="hamburger-toggle" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}><ObliqueIcon name="menu" size={24} /></button>
                    
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                            {/* Brand Logo Icon - shown when sidebar is hidden on non-LMS */}
                            {isHidden && !isLMS && (
                                <Link 
                                    to="/" 
                                    className="flex items-center justify-center p-1 rounded-lg hover:bg-[var(--bg-muted)] transition-colors cursor-pointer mr-0.5"
                                    title="Oblique Dashboard"
                                >
                                    <Logo iconOnly={true} size={26} />
                                </Link>
                            )}

                            {/* Desktop Sidebar Expand Toggle — only shown when sidebar is hidden */}
                            {isHidden && (
                                <button 
                                    onClick={toggleSidebar}
                                    title="Open sidebar"
                                    className="w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-muted)] transition-colors border-0 bg-transparent mr-1"
                                >
                                    <ObliqueIcon name="menu" size={18} />
                                </button>
                            )}

                            {/* Clean, Unboxed Tenant Brand Logo (System Design - Static identity; Tenant switching is strictly via Profile menu) */}
                            <div className="flex items-center select-none">
                                <TenantLogo
                                    tenant={currentTenant}
                                    company={currentCompany}
                                    height={24}
                                    showDropdownChevron={false}
                                    onClick={undefined}
                                    title={currentTenant?.name || currentCompany?.name || "Workspace"}
                                />
                            </div>
                        </div>
                        {/* Unified System Design Module Tag matching QMS, CRM, Analytics */}
                        {activeModule && (
                            <div className="border-l border-[var(--border)] pl-2 text-[9px] font-semibold text-[var(--text-tertiary)] uppercase tracking-wider">
                                {activeModule.name}
                            </div>
                        )}
                        <div className="flex-1" />
                        <button 
                            onClick={() => setIsCommandPaletteOpen(true)}
                            className="tour-step-search group flex items-center gap-2.5 h-[34px] w-[280px] px-3 rounded-lg bg-[var(--bg-subtle)] border border-[var(--border)] cursor-pointer text-[var(--text-tertiary)] text-[12.5px] font-medium transition-all hover:border-[var(--border-strong)] hover:text-[var(--text-secondary)] hover:bg-[var(--bg-muted)] focus:outline-none"
                        >
                            <ObliqueIcon name="search" size={15} className="opacity-70 group-hover:opacity-100 transition-opacity shrink-0" />
                            <span className="flex-1 text-left truncate">Search tools, deals, SOPs...</span>
                            <span className="text-[10px] font-semibold text-[var(--text-tertiary)] bg-[var(--bg-surface)] px-1.5 py-0.5 rounded border border-[var(--border)] opacity-80 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                                {navigator.platform?.toLowerCase().includes('mac') ? '⌘ K' : 'Ctrl K'}
                            </span>
                        </button>
                    </div>

                    <div className="flex items-center gap-2 ml-4 shrink-0">
                        {/* Core Symmetrical System Triggers */}
                        <ThemeToggle floating={false} />
                        <NotificationBell />
                        <AppLauncher />

                        <div className="h-5 w-px bg-[var(--border)] mx-1" />
                        <div 
                            onClick={() => setIsProfilePopupOpen(!isProfilePopupOpen)} 
                            className="tour-step-profile cursor-pointer flex items-center justify-center rounded-full hover:ring-2 hover:ring-[var(--accent)]/30 transition-all"
                            title="Profile & Workspace Settings"
                        >
                            <div className="w-[34px] h-[34px] rounded-full overflow-hidden border border-[var(--border)] bg-[var(--bg-muted)] flex items-center justify-center">
                                {userData?.photoURL ? (
                                    <img src={userData.photoURL} alt="User Avatar" className="w-full h-full object-cover" />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center bg-[var(--accent)] text-white text-xs font-semibold">
                                        {(userData?.displayName || 'U')[0].toUpperCase()}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </header>
                )}
                {/* SMART TAB BAR - Subtle grey version */}
                {showSmartTabs && !isAIPlatform && (
                    <div className="desktop-only flex items-center gap-1 pt-2 px-3 pb-0 bg-[var(--bg-surface)] border-b border-[var(--border)] overflow-x-auto z-20 h-[34px]">
                        {/* Smart Tab Bar - Content */}
                        <AnimatePresence mode="popLayout">
                            {tabs.map(tab => (
                                <motion.div 
                                    key={tab.id} 
                                    layout
                                    initial={{ opacity: 0, y: 10, scale: 0.9 }}
                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.8, x: -20, transition: { duration: 0.1 } }}
                                    onClick={() => switchTab(tab)} 
                                    className={`flex items-center gap-1.5 py-1 px-2.5 min-w-20 max-w-40 rounded-t-md border-b-0 cursor-pointer relative top-[1px] transition-all duration-200 shrink-0 ${
                                        tab.active
                                            ? 'bg-[var(--bg-page)] border border-[var(--border)] opacity-100'
                                            : 'bg-transparent border border-transparent opacity-60 hover:opacity-90'
                                    }`}
                                >
                                    <ObliqueIcon name={tab.icon} size={11} color={tab.active ? 'var(--text-secondary)' : 'var(--text-tertiary)'} />
                                    <span className={`text-[10.5px] tracking-tight flex-1 whitespace-nowrap overflow-hidden text-ellipsis ${
                                        tab.active ? 'font-bold text-[var(--text-secondary)]' : 'font-medium text-[var(--text-tertiary)]'
                                    }`}>
                                        {tab.title}
                                    </span>
                                    {(tab.active || tabs.length > 1) && (
                                        <button 
                                            onClick={(e) => closeTab(e, tab.id)} 
                                            className="bg-transparent border-none p-px rounded flex items-center justify-center transition-colors cursor-pointer ml-1 opacity-50 hover:opacity-100 hover:bg-[var(--bg-muted)]"
                                            title="Close Tab"
                                        >
                                            <ObliqueIcon 
                                                name="close" 
                                                size={12} 
                                                color="var(--text-tertiary)" 
                                            />
                                        </button>
                                    )}
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    </div>
                )}



                <main className={`flex-1 overflow-hidden ${isAIPlatform ? '' : 'p-0'} relative bg-[var(--bg-page)]`}>
                    <div className="flex flex-col w-full h-full overflow-auto">
                        <Suspense fallback={null}>
                            <motion.div
                                key={location.pathname.split('/')[1] || location.pathname}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.12, ease: 'easeOut' }}
                                className="flex-1 flex flex-col"
                            >
                                {outlet}
                            </motion.div>
                        </Suspense>
                    </div>
                </main>

            </div>

            <WalkthroughTour />

            <MobileLayout />

            {/* Oblique-Style Profile & Workspace Popup */}
            <ProfileMenu
                isOpen={isProfilePopupOpen}
                onClose={() => setIsProfilePopupOpen(false)}
                onCreateTenant={() => setIsCreateTenantModalOpen(true)}
            />

            {/* ═══════ Command Palette — ⌘K Spotlight ═══════ */}
            <GlobalSearch 
                isOpen={isCommandPaletteOpen} 
                onClose={() => setIsCommandPaletteOpen(false)} 
            />



            <CreateTenantModal 
                isOpen={isCreateTenantModalOpen} 
                onClose={() => setIsCreateTenantModalOpen(false)} 
            />

            {/* Integrated AI Assistant Button */}
            {!isAIPlatform && (
                <FloatingAIAssistantButton />
            )}
        </div>
    );
}
